/**
 * storefrontTheme — the ONE vocabulary the storefront renderer and the
 * Design Studio editor share (v5.66 facelift).
 *
 * Before this module the editor offered fonts nothing loaded (DM Sans — the
 * renderer's own default — Plus Jakarta Sans, Nunito, Poppins), section
 * colour rows for sections that did not exist (`contact`) and none for
 * sections that did (how-it-works, faq, passes, punchline, closing, footer),
 * and a hero CTA anchor (`contact`) no element answered to. Two lists that
 * must agree become one list: the editor's options are DERIVED from here
 * and a contract asserts the renderer reads exactly these ids.
 */

// ── Fonts ────────────────────────────────────────────────────────────────
// Every family the editor offers is loadable by `useStorefrontFonts`
// (Google Fonts css2). `axis` is the css2 spec for that family.
export const HEADING_FONTS = [
  { family: 'Quicksand', axis: 'wght@300..700', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Playfair Display', axis: 'ital,wght@0,400..900;1,400..900', stack: 'ui-serif, Georgia, serif' },
  { family: 'Fraunces', axis: 'ital,opsz,wght@0,9..144,300..900;1,9..144,300..900', stack: 'ui-serif, Georgia, serif' },
  { family: 'DM Serif Display', axis: 'ital@0;1', stack: 'ui-serif, Georgia, serif' },
  { family: 'Cormorant Garamond', axis: 'ital,wght@0,400..700;1,400..700', stack: 'ui-serif, Georgia, serif' },
  { family: 'Inter Tight', axis: 'wght@400..900', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Outfit', axis: 'wght@300..900', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Sora', axis: 'wght@300..800', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Plus Jakarta Sans', axis: 'wght@300..800', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Inter', axis: 'wght@300..800', stack: 'ui-sans-serif, system-ui, sans-serif' },
];

export const BODY_FONTS = [
  { family: 'Quicksand', axis: 'wght@300..700', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'DM Sans', axis: 'ital,opsz,wght@0,9..40,300..700;1,9..40,300..700', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Inter', axis: 'wght@300..800', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Plus Jakarta Sans', axis: 'wght@300..800', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Manrope', axis: 'wght@300..800', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Outfit', axis: 'wght@300..900', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Nunito', axis: 'ital,wght@0,300..900;1,300..900', stack: 'ui-sans-serif, system-ui, sans-serif' },
  { family: 'Poppins', axis: 'wght@300;400;500;600;700', stack: 'ui-sans-serif, system-ui, sans-serif' },
];

// The handwritten / script accent used for the italic phrase in every headline.
export const SCRIPT_FONTS = [
  { family: 'Caveat', axis: 'wght@400..700', stack: 'cursive' },
  { family: 'Pacifico', axis: 'wght@400', stack: 'cursive' },
  { family: 'Dancing Script', axis: 'wght@400..700', stack: 'cursive' },
  { family: 'Kalam', axis: 'wght@300;400;700', stack: 'cursive' },
  { family: 'Shadows Into Light', axis: 'wght@400', stack: 'cursive' },
];

export const DEFAULT_HEADING_FONT = 'Playfair Display';
export const DEFAULT_BODY_FONT = 'DM Sans';
export const DEFAULT_SCRIPT_FONT = 'Caveat';

const ALL_FONTS = [...HEADING_FONTS, ...BODY_FONTS, ...SCRIPT_FONTS];

export const fontSpec = (family) => ALL_FONTS.find((f) => f.family === family) || null;

export const fontStack = (family, fallbackFamily) => {
  const spec = fontSpec(family) || fontSpec(fallbackFamily);
  const fam = spec ? spec.family : (family || fallbackFamily);
  const stack = spec ? spec.stack : 'ui-sans-serif, system-ui, sans-serif';
  return `"${fam}", ${stack}`;
};

// ── Sections ─────────────────────────────────────────────────────────────
// Every section the renderer styles through `getSectionStyle(id)`, in page
// order. The editor's "Section colours" rows and the hero-CTA anchor
// options are built from this list; a contract pins the renderer reads
// exactly these ids and that every anchorable id is a real element id.
export const STOREFRONT_SECTIONS = [
  { id: 'hero', label: 'Hero', anchor: false },
  { id: 'showcase', label: 'From the kitchen (dish showcase)', anchor: true },
  { id: 'how-it-works', label: 'How it works', anchor: true },
  { id: 'passes', label: 'Meal passes', anchor: true },
  { id: 'plans', label: 'Meal plans / subscriptions', anchor: true },
  { id: 'catering', label: 'Catering', anchor: true },
  { id: 'menu', label: 'Menu', anchor: true },
  { id: 'story', label: 'Our story', anchor: true },
  { id: 'gallery', label: 'Photos', anchor: true },
  { id: 'testimonials', label: 'Reviews', anchor: true },
  { id: 'punchline', label: 'Punchline slab', anchor: false },
  { id: 'faq', label: 'FAQ', anchor: true },
  { id: 'closing', label: 'Closing slab', anchor: false },
  { id: 'footer', label: 'Footer', anchor: true },
];

export const HERO_CTA_ANCHORS = STOREFRONT_SECTIONS.filter((s) => s.anchor).map((s) => ({ value: s.id, label: s.label }));

export const CORNER_STYLES = [
  { value: 'soft', label: 'Soft (default)', radius: '1.25rem' },
  { value: 'round', label: 'Round', radius: '2rem' },
  { value: 'sharp', label: 'Sharp', radius: '0.25rem' },
];

export const HERO_LAYOUTS = [
  { value: 'cover', label: 'Cover — your words over one full-width photo (default)' },
  { value: 'collage', label: 'Collage — copy beside a mosaic of your photos (needs 3+, else the single photo)' },
  { value: 'split', label: 'Split — copy beside one photo' },
  { value: 'centered', label: 'Centered — copy over the photo' },
];

// How every headline's accent phrase is set: coloured in the heading face
// (the modern storefront default) or in the handwritten accent font.
export const ACCENT_STYLES = [
  { value: 'highlight', label: 'Highlight — the phrase in your brand colour (default)' },
  { value: 'script', label: 'Handwritten — the phrase in the accent font' },
];

// ── Colour helpers ───────────────────────────────────────────────────────
const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
const hexToRgb = (hex) => {
  const h = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const rgbToHex = ([r, g, b]) => `#${[r, g, b].map((n) => clamp(n).toString(16).padStart(2, '0')).join('')}`;

export const mix = (hex, withHex, pct) => {
  const a = hexToRgb(hex);
  const b = hexToRgb(withHex);
  if (!a || !b) return hex;
  return rgbToHex(a.map((v, i) => v + (b[i] - v) * pct));
};
export const darken = (hex, pct) => mix(hex, '#000000', pct);
export const lighten = (hex, pct) => mix(hex, '#ffffff', pct);

// WCAG relative luminance — decides ink-on-light vs paper-on-dark for a surface.
export const luminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 1;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const isDark = (hex) => luminance(hex) < 0.35;
// The text colour that reads on a given surface (ink on light, paper on dark).
export const inkFor = (surfaceHex, ink, paper) => (isDark(surfaceHex) ? paper : ink);
// WCAG contrast ratio between two colours (1..21).
export const contrastRatio = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
// The candidate that reads BEST on a surface. Page ink and primary are both
// merchant-editable, so "ink unless the surface is dark" is not enough: a
// light ink on a light primary read at 1.45:1. Ties keep candidate order.
export const bestTextOn = (surfaceHex, candidates) =>
  candidates.filter(Boolean).reduce((best, c) => (contrastRatio(c, surfaceHex) > contrastRatio(best, surfaceHex) ? c : best));

// ── The token set ────────────────────────────────────────────────────────
// One derivation for every storefront surface (main page, catalogue, about,
// contact) so they can never disagree.
export const buildTokens = (brand = {}) => {
  const primary = brand.primary_color || '#7a1e2f';
  const accent = brand.accent_color || lighten(primary, 0.55);
  const paper = brand.page_bg_color || '#faf6f0';
  const ink = brand.ink_color || '#1a1613';
  const paperAlt = isDark(paper) ? lighten(paper, 0.06) : darken(paper, 0.035);
  const dark = brand.dark_surface_color || '#161210';
  const corner = CORNER_STYLES.find((c) => c.value === brand.corner_style) || CORNER_STYLES[0];
  const headingFont = brand.heading_font || DEFAULT_HEADING_FONT;
  const bodyFont = brand.body_font || DEFAULT_BODY_FONT;
  const scriptFont = brand.accent_font || DEFAULT_SCRIPT_FONT;
  return {
    primary, accent, paper, paperAlt, ink, dark,
    // page tints — the soft washes sections alternate through (a page that
    // is one flat colour top to bottom reads as unfinished)
    tint: mix(paper, primary, isDark(paper) ? 0.12 : 0.06),
    tintAccent: mix(paper, accent, isDark(paper) ? 0.16 : 0.16),
    accentStyle: ACCENT_STYLES.some((a) => a.value === brand.accent_style) ? brand.accent_style : 'highlight',
    inkMuted: isDark(paper) ? lighten(paper, 0.55) : lighten(ink, 0.42),
    line: isDark(paper) ? lighten(paper, 0.16) : darken(paper, 0.10),
    onPrimary: bestTextOn(primary, [ink, '#ffffff', '#1a1613', paper]),
    onDark: bestTextOn(dark, ['#faf6f0', paper, ink, '#1a1613']),
    radius: corner.radius,
    headingFont, bodyFont, scriptFont,
    headingStack: fontStack(headingFont, DEFAULT_HEADING_FONT),
    bodyStack: fontStack(bodyFont, DEFAULT_BODY_FONT),
    scriptStack: fontStack(scriptFont, DEFAULT_SCRIPT_FONT),
    heroLayout: HERO_LAYOUTS.some((h) => h.value === brand.hero_layout) ? brand.hero_layout : 'cover',
  };
};

// The CSS custom properties the page shell exposes — every section reads
// these instead of a literal, which is what lets Design Studio restyle the
// whole page from one form.
export const tokensToCssVars = (t) => ({
  '--sf-primary': t.primary,
  '--sf-accent': t.accent,
  '--sf-paper': t.paper,
  '--sf-paper-alt': t.paperAlt,
  '--sf-tint': t.tint,
  '--sf-tint-accent': t.tintAccent,
  '--sf-ink': t.ink,
  '--sf-ink-muted': t.inkMuted,
  '--sf-line': t.line,
  '--sf-dark': t.dark,
  '--sf-on-primary': t.onPrimary,
  '--sf-on-dark': t.onDark,
  '--sf-radius': t.radius,
  '--sf-heading': t.headingStack,
  '--sf-body': t.bodyStack,
  '--sf-script': t.scriptStack,
});
